import { useState } from "react";
import { FiCheckCircle, FiMail, FiMapPin, FiPhone } from "react-icons/fi";

/** Round cooperative logo with an initial-letter fallback if the image is missing or fails. */
export function OrgLogo({ company, size = "md" }) {
  const [failed, setFailed] = useState(false);
  const name = company?.name || "";
  const cls = `ax-logo${size === "lg" ? " ax-logo--lg" : ""}`;
  if (company?.logo_url && !failed) {
    return (
      <img
        className={cls}
        src={company.logo_url}
        alt={`${name} logo`}
        onError={() => setFailed(true)}
      />
    );
  }
  return (
    <span className={cls} aria-hidden="true">
      {name ? name.charAt(0).toUpperCase() : ""}
    </span>
  );
}

function OrgText({ company, loading }) {
  if (loading && !company) {
    return (
      <div className="ax-orgtext" aria-busy="true">
        <span className="ax-skel" style={{ width: 180, height: 16 }} />
        <span className="ax-skel" style={{ width: 120, height: 12, marginTop: 8 }} />
      </div>
    );
  }
  return (
    <div className="ax-orgtext">
      <p className="ax-orgname">{company?.name}</p>
      {company?.address ? <p className="ax-orgaddr">{company.address}</p> : null}
    </div>
  );
}

/**
 * Page frame for login, forgot password and sign-up.
 * Phones get a one-line cooperative header; desktops get a brand panel on the left.
 * The form paints straight away; only the cooperative name and logo wait for the network.
 */
export default function AuthShell({
  company,
  loading = false,
  branches = [],
  points = [],
  wide = false,
  children,
}) {
  const hasBranches = Array.isArray(branches) && branches.length > 0;
  return (
    <div className="ax ax-shell">
      <aside className="ax-brand" aria-label="About this cooperative">
        <div className="ax-brand-top">
          <OrgLogo company={company} size="lg" />
          <OrgText company={company} loading={loading} />
        </div>

        {hasBranches || points.length > 0 ? (
          <div className="ax-brand-mid">
            {hasBranches ? (
              <div className="ax-brand-block">
                <h2>Branches</h2>
                <ul className="ax-branchlist">
                  {branches.map((b) => (
                    <li key={b.id}>
                      <FiMapPin aria-hidden="true" size={14} />
                      {b.name}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {points.length > 0 ? (
              <ul className="ax-points">
                {points.map((text) => (
                  <li key={text}>
                    <FiCheckCircle aria-hidden="true" size={18} />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null}

        {company?.phone || company?.email ? (
          <div className="ax-brand-block ax-brand-bottom">
            <h2>Need help?</h2>
            <ul className="ax-contact">
              {company.phone ? (
                <li>
                  <FiPhone aria-hidden="true" size={16} />
                  <a href={`tel:${company.phone}`}>{company.phone}</a>
                </li>
              ) : null}
              {company.email ? (
                <li>
                  <FiMail aria-hidden="true" size={16} />
                  <a href={`mailto:${company.email}`}>{company.email}</a>
                </li>
              ) : null}
            </ul>
          </div>
        ) : null}
      </aside>

      <main className="ax-main">
        <div className="ax-mobilebrand">
          <OrgLogo company={company} />
          <OrgText company={company} loading={loading} />
        </div>
        <div className={`ax-content${wide ? " ax-content--wide" : ""}`}>{children}</div>
      </main>
    </div>
  );
}
